import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  BarChart3,
  MessageCircle,
  Users2,
  TrendingUp,
  CheckCircle,
  Clock,
  UserCheck,
  Shield,
  Search,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FeedbackDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [abaAtiva, setAbaAtiva] = useState('geral');

  const [stats90, setStats90] = useState({ 
    meus: { total: 0, quickFeedback: 0, checkins: 0 },
    global: { total: 0, quickFeedback: 0, checkins: 0 }
  });
  const [stats180, setStats180] = useState({ pendentes: 0, concluidos: 0, total: 0 });
  const [stats360, setStats360] = useState({ pendentes: 0, concluidos: 0, total: 0, ciclosAtivos: 0 });
  
  const [loading, setLoading] = useState(true);

  // Admin state
  const [adminTab, setAdminTab] = useState('90');
  const [feedbacks90, setFeedbacks90] = useState([]);
  const [feedbacks180, setFeedbacks180] = useState([]);
  const [feedbacks360, setFeedbacks360] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [filtroAplicador, setFiltroAplicador] = useState('');
  const [filtroDestinatario, setFiltroDestinatario] = useState('');
  const [expandidoId, setExpandidoId] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (isAdmin && abaAtiva === 'admin') {
      carregarFeedbacksAdmin();
    }
  }, [isAdmin, abaAtiva]);

  const carregarFeedbacksAdmin = async () => {
    setLoadingAdmin(true);
    const token = localStorage.getItem('token');
    try {
      const [res90, res180, res360] = await Promise.all([
        axios.get(`${API}/api/feedback/admin/90`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/feedback/admin/180`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/feedback/admin/360`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFeedbacks90(res90.data || []);
      setFeedbacks180(res180.data || []);
      setFeedbacks360(res360.data || []);
    } catch (error) {
      console.error('Erro ao carregar feedbacks admin:', error);
      toast.error('Erro ao carregar feedbacks');
    } finally {
      setLoadingAdmin(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      try {
        const meusQuickResponse = await axios.get(`${API}/api/feedback90/quick/historico`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const meusCheckinsResponse = await axios.get(`${API}/api/feedback90/1on1/historico`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const meusQuick = meusQuickResponse.data?.length || 0;
        const meusCheckins = meusCheckinsResponse.data?.length || 0;
        
        const stats90Response = await axios.get(`${API}/api/feedback90/stats/global`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data90 = stats90Response.data;
        
        setStats90({ 
          meus: {
            total: meusQuick + meusCheckins,
            quickFeedback: meusQuick,
            checkins: meusCheckins
          },
          global: {
            total: data90.total || 0, 
            quickFeedback: data90.quick_feedbacks || 0, 
            checkins: data90.checkins_1on1 || 0
          }
        });
      } catch (error) {
        setStats90({ 
          meus: { total: 0, quickFeedback: 0, checkins: 0 },
          global: { total: 0, quickFeedback: 0, checkins: 0 }
        });
      }
      
      try {
        const stats180Response = await axios.get(`${API}/api/feedback180-v2/stats/global`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data180 = stats180Response.data;
        setStats180({ 
          pendentes: data180.pendentes || 0, 
          concluidos: data180.concluidos || 0, 
          total: data180.total || 0
        });
      } catch (error) {
        setStats180({ pendentes: 0, concluidos: 0, total: 0 });
      }
      
      try {
        const stats360Response = await axios.get(`${API}/api/feedback360/stats/global`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data360 = stats360Response.data;
        setStats360({ 
          pendentes: data360.pendentes || 0, 
          concluidos: data360.concluidos || 0, 
          total: data360.total || 0,
          ciclosAtivos: data360.ciclos_ativos || 0
        });
      } catch (error) {
        setStats360({ pendentes: 0, concluidos: 0, total: 0, ciclosAtivos: 0 });
      }
      
    } catch (error) {
      console.error('Erro geral ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = async (tipo, id) => {
    if (!window.confirm('Tem certeza que deseja cancelar este feedback?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/feedback/admin/cancelar`, 
        { tipo, id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Feedback cancelado com sucesso');
      carregarFeedbacksAdmin();
    } catch (error) {
      toast.error('Erro ao cancelar feedback');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'cancelado') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Cancelado</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Ativo</span>;
  };

  const getTipoLabel = (subtipo) => {
    const labels = {
      kudos: 'Kudos', keep_doing: 'Continue Fazendo', improve: 'Melhoria',
      autoavaliacao: 'Autoavaliação', gestor_avalia_colaborador: 'Gestor → Colaborador',
      colaborador_avalia_gestor: 'Colaborador → Gestor',
      gestor: 'Gestor', par: 'Par', cliente_interno: 'Cliente Interno', subordinado: 'Subordinado'
    };
    return labels[subtipo] || subtipo;
  };

  const getTipoCor = (tipo) => {
    const cores = { '90': 'bg-blue-100 text-blue-800', '180': 'bg-purple-100 text-purple-800', '360': 'bg-green-100 text-green-800' };
    return cores[tipo] || 'bg-gray-100 text-gray-800';
  };

  const filtrarFeedbacks = (lista) => {
    return lista.filter(fb => {
      if (filtroAplicador && !fb.aplicador_nome?.toLowerCase().includes(filtroAplicador.toLowerCase())) return false;
      if (filtroDestinatario && !fb.destinatario_nome?.toLowerCase().includes(filtroDestinatario.toLowerCase())) return false;
      return true;
    });
  };

  const renderAdminPanel = () => {
    const tabs = ['90', '180', '360'];
    const tabLabels = { '90': 'Feedback 90º', '180': 'Feedback 180º', '360': 'Feedback 360º' };
    const tabAtivaClass = { '90': 'border-blue-600 text-blue-700', '180': 'border-purple-600 text-purple-700', '360': 'border-green-600 text-green-700' };
    const tabCountClass = { '90': 'bg-blue-100 text-blue-700', '180': 'bg-purple-100 text-purple-700', '360': 'bg-green-100 text-green-700' };
    const dataMap = { '90': feedbacks90, '180': feedbacks180, '360': feedbacks360 };
    const currentData = filtrarFeedbacks(dataMap[adminTab] || []);

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Shield className="w-6 h-6 mr-2 text-gray-600" />
            Gestão de Feedbacks
          </h2>
        </div>

        {/* Sub-abas: 90, 180, 360 */}
        <div className="flex space-x-2 border-b border-gray-200">
          {tabs.map(t => {
            const ativa = adminTab === t;
            return (
              <button key={t} onClick={() => setAdminTab(t)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  ativa ? tabAtivaClass[t] : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tabLabels[t]}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  ativa ? tabCountClass[t] : 'bg-gray-100 text-gray-600'
                }`}>
                  {dataMap[t]?.length || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-4 items-end bg-gray-50 p-4 rounded-lg">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-600 mb-1 font-medium">Filtrar por Aplicador</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={filtroAplicador} onChange={e => setFiltroAplicador(e.target.value)}
                placeholder="Nome do aplicador..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-600 mb-1 font-medium">Filtrar por Destinatário</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={filtroDestinatario} onChange={e => setFiltroDestinatario(e.target.value)}
                placeholder="Nome do destinatário..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <button onClick={() => { setFiltroAplicador(''); setFiltroDestinatario(''); }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Limpar Filtros
          </button>
        </div>

        {/* Lista de Feedbacks */}
        {loadingAdmin ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-3 text-sm">Carregando feedbacks...</p>
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Nenhum feedback encontrado</p>
          </div>
        ) : (
          <div className="space-y-3">
            {currentData.map((fb) => {
              const isCancelado = fb.status === 'cancelado';
              const isExpandido = expandidoId === `${fb.tipo}-${fb.id}`;
              return (
                <div key={`${fb.tipo}-${fb.id}`} 
                  className={`bg-white border rounded-lg transition-all ${
                    isCancelado ? 'border-red-200 opacity-70' : 'border-gray-200 hover:shadow-sm'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2 flex-wrap gap-y-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTipoCor(fb.tipo)}`}>
                            {fb.tipo}º
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {getTipoLabel(fb.subtipo)}
                          </span>
                          {getStatusBadge(fb.status)}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span><strong>Aplicador:</strong> {fb.aplicador_nome}</span>
                          <span><strong>Destinatário:</strong> {fb.destinatario_nome}</span>
                          <span className="text-xs text-gray-400">
                            {fb.data ? new Date(fb.data).toLocaleString('pt-BR') : ''}
                          </span>
                        </div>
                        {fb.conteudo && (
                          <p className="mt-2 text-sm text-gray-700 line-clamp-2">{fb.conteudo}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button onClick={() => setExpandidoId(isExpandido ? null : `${fb.tipo}-${fb.id}`)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Ver detalhes"
                        >
                          {isExpandido ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {!isCancelado && (
                          <button onClick={() => handleCancelar(fb.tipo, fb.id)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancelar feedback"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {isExpandido && (
                    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 rounded-b-lg">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-gray-500">ID:</span> <span className="text-gray-700 font-mono text-xs">{fb.id}</span></div>
                        <div><span className="text-gray-500">Status:</span> <span>{getStatusBadge(fb.status)}</span></div>
                        <div><span className="text-gray-500">Aplicador:</span> <span className="text-gray-700">{fb.aplicador_nome} ({fb.aplicador_id})</span></div>
                        <div><span className="text-gray-500">Destinatário:</span> <span className="text-gray-700">{fb.destinatario_nome} ({fb.destinatario_id})</span></div>
                        {fb.data && <div><span className="text-gray-500">Data:</span> <span className="text-gray-700">{new Date(fb.data).toLocaleString('pt-BR')}</span></div>}
                        {fb.conteudo && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Conteúdo:</span>
                            <p className="text-gray-700 mt-1 bg-white p-3 rounded border">{fb.conteudo}</p>
                          </div>
                        )}
                        {fb.cancelado_por && (
                          <div className="col-span-2 p-2 bg-red-50 rounded border border-red-200">
                            <span className="text-red-700 text-xs">
                              Cancelado por {fb.cancelado_por} em {fb.cancelado_em ? new Date(fb.cancelado_em).toLocaleString('pt-BR') : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-200 rounded-lg h-48"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard de Feedback</h1>
        <p className="text-gray-600 mt-1">Visão geral consolidada de todos os módulos de feedback</p>
      </div>

      {/* Abas principais */}
      <div className="flex space-x-1 border-b border-gray-200">
        <button onClick={() => setAbaAtiva('geral')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            abaAtiva === 'geral'
              ? 'bg-white text-blue-700 border-l border-r border-t border-gray-200 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline mr-1.5" />
          Visão Geral
        </button>
        {isAdmin && (
          <button onClick={() => setAbaAtiva('admin')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              abaAtiva === 'admin'
                ? 'bg-white text-red-700 border-l border-r border-t border-gray-200 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-1.5" />
            Gestão de Feedbacks
          </button>
        )}
      </div>

      {/* Conteúdo das abas */}
      {abaAtiva === 'geral' ? (
        <>
          {/* Cards por Módulo */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg p-6 border-2 border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-blue-900">Feedback 90º</h2>
                    <p className="text-sm text-blue-700">Contínuo e Check-ins</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mt-6">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Total de Feedbacks</span>
                    <span className="text-2xl font-bold text-blue-600">{stats90.global.total}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border-l-4 border-blue-400">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-blue-700">Meus Quick Feedbacks</span>
                    <span className="text-lg font-bold text-blue-600">{stats90.meus.quickFeedback}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Total no Sistema</span>
                    <span className="font-semibold">{stats90.global.quickFeedback}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border-l-4 border-blue-400">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-blue-700">Meus Check-ins 1:1</span>
                    <span className="text-lg font-bold text-blue-600">{stats90.meus.checkins}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Total no Sistema</span>
                    <span className="font-semibold">{stats90.global.checkins}</span>
                  </div>
                </div>
              </div>

              <button onClick={() => window.location.href = '/feedback/90/frequency-dashboard'}
                className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Acessar Módulo
              </button>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-lg p-6 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-600 p-3 rounded-lg">
                    <UserCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-purple-900">Feedback 180º</h2>
                    <p className="text-sm text-purple-700">Gestor ↔ Colaborador</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mt-6">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Total de Avaliações</span>
                    <span className="text-2xl font-bold text-purple-600">{stats180.total}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 flex items-center">
                      <Clock className="w-3 h-3 mr-1" /> Pendentes
                    </span>
                    <span className="text-lg font-semibold text-orange-600">{stats180.pendentes}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" /> Concluídos
                    </span>
                    <span className="text-lg font-semibold text-green-600">{stats180.concluidos}</span>
                  </div>
                </div>
              </div>

              <button onClick={() => window.location.href = '/feedback/180-v2/gerenciar'}
                className="mt-4 w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Acessar Módulo
              </button>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-lg p-6 border-2 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-600 p-3 rounded-lg">
                    <Users2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-green-900">Feedback 360º</h2>
                    <p className="text-sm text-green-700">Avaliação Multidirecional</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mt-6">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Total de Avaliações</span>
                    <span className="text-2xl font-bold text-green-600">{stats360.total}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 flex items-center">
                      <TrendingUp className="w-3 h-3 mr-1" /> Ciclos Ativos
                    </span>
                    <span className="text-lg font-semibold text-blue-600">{stats360.ciclosAtivos}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div>
                      <div className="text-xs text-gray-600 flex items-center justify-center">
                        <Clock className="w-3 h-3 mr-1" /> Pendentes
                      </div>
                      <div className="text-lg font-semibold text-orange-600">{stats360.pendentes}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 mr-1" /> Concluídos
                      </div>
                      <div className="text-lg font-semibold text-green-600">{stats360.concluidos}</div>
                    </div>
                  </div>
                </div>
              </div>

              <button onClick={() => window.location.href = '/feedback/360'}
                className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Acessar Módulo
              </button>
            </div>
          </div>

          {/* Resumo Geral */}
          <div className="bg-white rounded-xl shadow-lg p-6 border">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Resumo Geral</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{stats90.global.total}</div>
                <div className="text-sm text-gray-600 mt-1">Feedbacks 90º</div>
              </div>

              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">{stats180.total}</div>
                <div className="text-sm text-gray-600 mt-1">Avaliações 180º</div>
              </div>

              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{stats360.total}</div>
                <div className="text-sm text-gray-600 mt-1">Avaliações 360º</div>
              </div>

              <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                <div className="text-3xl font-bold text-orange-600">
                  {stats180.pendentes + stats360.pendentes}
                </div>
                <div className="text-sm text-gray-600 mt-1">Total Pendentes</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        renderAdminPanel()
      )}
    </div>
  );
};

export default FeedbackDashboard;
